import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { CheckCircle, ArrowLeft } from "lucide-react";

export const Route = createFileRoute("/marketing")({
  head: () => ({
    meta: [
      { title: "Kampanie marketingowe i social media — SkuteczneStrony" },
      {
        name: "description",
        content:
          "Prowadzimy social media, kampanie reklamowe Meta Ads i content marketing. Pakiety od 899 zł miesięcznie.",
      },
      { property: "og:title", content: "Marketing i social media — SkuteczneStrony" },
      {
        property: "og:description",
        content: "Pakiety Social Essentials i Social Dominator dla firm, które chcą rosnąć.",
      },
    ],
  }),
  component: MarketingPage,
});

const EASE = [0.16, 1, 0.3, 1] as const;

const SOCIAL_PLANS = [
  {
    badge: "Social Essentials",
    price: "899 zł",
    period: "miesięcznie",
    desc: "Wejście w social media bez chaosu. Stała obecność marki na jednym kanale.",
    features: [
      "Prowadzenie 1 kanału (Instagram lub Facebook)",
      "8 unikalnych postów miesięcznie",
      "Grafiki, copy i harmonogram publikacji",
      "Moderacja komentarzy i wiadomości",
      "Raport wyników raz w miesiącu",
    ],
    accent: false,
  },
  {
    badge: "Social Dominator",
    price: "1 699 zł",
    period: "miesięcznie",
    desc: "Pełna obecność na dwóch kanałach plus płatne kampanie. Dla firm, które chcą rosnąć.",
    features: [
      "Prowadzenie 2 kanałów (Instagram plus Facebook)",
      "16 postów i 4 rolki (Reels lub Shorts) miesięcznie",
      "Kampanie Meta Ads (budżet po stronie klienta)",
      "Community management, odpowiedzi do 12 h",
      "Raport wyników 2 razy w miesiącu",
    ],
    accent: true,
  },
];

const EXTRAS = [
  { name: "Sesja zdjęciowa produktowa", price: "od 699 zł" },
  { name: "Reels nagrywany u klienta (1 dzień)", price: "od 1 200 zł" },
  { name: "Google Ads — konfiguracja i prowadzenie", price: "od 599 zł miesięcznie" },
  { name: "Newsletter (1 wysyłka miesięcznie)", price: "od 399 zł" },
  { name: "Współpraca z influencerami (selekcja i brief)", price: "od 499 zł" },
];

function MarketingPage() {
  return (
    <main className="relative min-h-screen overflow-x-clip bg-[#030508] font-sans text-slate-200 selection:bg-[#00F5A0]/30">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[60vh] bg-[radial-gradient(ellipse_at_top,rgba(0,245,160,0.15),transparent_70%)]" />

      <header className="relative z-10 mx-auto flex max-w-7xl items-center justify-between px-5 py-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-bold text-white backdrop-blur-xl transition hover:border-[#00F5A0]/40"
        >
          <ArrowLeft size={16} />
          Strona główna
        </Link>
        <Link
          to="/"
          hash="cta"
          className="rounded-full bg-[#00F5A0] px-5 py-2 text-sm font-black text-black shadow-lg shadow-[#00F5A0]/30 hover:brightness-110"
        >
          Porozmawiajmy
        </Link>
      </header>

      <section className="relative z-10 mx-auto max-w-5xl px-5 pb-16 pt-10 text-center md:pb-24 md:pt-20">
        <span className="mb-4 inline-block rounded-full border border-[#00F5A0]/30 bg-[#00F5A0]/5 px-5 py-1.5 text-xs uppercase tracking-[0.3em] text-[#00F5A0] backdrop-blur-md">
          Marketing i social media
        </span>
        <h1 className="text-4xl font-black leading-[1.05] tracking-tight text-white md:text-7xl">
          Twoja marka.{" "}
          <span
            className="bg-gradient-to-r from-[#00F5A0] to-[#6366F1] bg-clip-text text-transparent"
            style={{ filter: "drop-shadow(0 0 30px rgba(0,245,160,0.3))" }}
          >
            Widoczna codziennie.
          </span>
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-400 md:text-lg">
          Tworzymy treści, prowadzimy kanały i odpalamy kampanie reklamowe. Pracujemy w miesięcznych pakietach — bez ukrytych kosztów i bez umów na rok.
        </p>
      </section>

      <section className="relative z-10 mx-auto max-w-7xl px-5 pb-20 md:pb-28">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {SOCIAL_PLANS.map((p) => (
            <motion.div
              key={p.badge}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: EASE }}
              className={`relative flex min-w-0 flex-col overflow-hidden rounded-3xl p-7 md:p-10 ${
                p.accent
                  ? "border border-[#00F5A0]/50 bg-gradient-to-br from-[#04110b] via-[#02050A] to-[#020305]"
                  : "border border-white/10 bg-white/[0.02]"
              }`}
              style={p.accent ? { boxShadow: "0 0 80px rgba(0,245,160,0.12)" } : undefined}
            >
              <span
                className={`mb-3 text-xs font-bold uppercase tracking-[0.25em] ${
                  p.accent ? "text-[#00F5A0]" : "text-slate-500"
                }`}
              >
                {p.badge}
              </span>
              <p className="text-sm leading-relaxed text-slate-400">{p.desc}</p>
              <div className="mt-6 flex items-baseline gap-2 border-y border-white/10 py-5">
                <p className="text-4xl font-black tabular-nums text-white md:text-5xl">{p.price}</p>
                <span className="text-xs font-bold uppercase tracking-widest text-slate-400">
                  {p.period}
                </span>
              </div>
              <ul className="mt-6 mb-8 flex flex-col gap-3">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-slate-300">
                    <CheckCircle
                      size={18}
                      className={`mt-0.5 shrink-0 ${p.accent ? "text-[#00F5A0]" : "text-slate-500"}`}
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/"
                hash="cta"
                className={`mt-auto w-full rounded-full py-4 text-center text-sm font-black uppercase tracking-wider transition ${
                  p.accent
                    ? "bg-[#00F5A0] text-black shadow-lg shadow-[#00F5A0]/40 hover:brightness-125"
                    : "border border-white/20 text-white hover:bg-white hover:text-black"
                }`}
              >
                Chcę ten pakiet
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="mt-16">
          <h2 className="mb-6 text-center text-2xl font-black text-white md:text-3xl">
            Dodatki marketingowe
          </h2>
          <div className="mx-auto max-w-3xl overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]">
            {EXTRAS.map((e, i) => (
              <div
                key={e.name}
                className={`flex items-center justify-between gap-4 px-5 py-4 text-sm md:text-base ${
                  i === 0 ? "" : "border-t border-white/5"
                }`}
              >
                <span className="text-slate-300">{e.name}</span>
                <span className="shrink-0 text-right font-black tabular-nums text-[#00F5A0]">
                  {e.price}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="relative z-10 mx-auto max-w-7xl border-t border-white/10 px-5 py-10 text-center text-sm text-slate-500">
        © 2026 SkuteczneStrony. Wszelkie prawa zastrzeżone.
      </footer>
    </main>
  );
}